import { useState, useCallback, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

/**
 * [T68P1-b] Intercept sheet close when form is dirty.
 *
 * Returns:
 * - `guardedOnOpenChange` — drop-in replacement for the raw `onOpenChange`
 * - `showDiscard`         — controls the DiscardChangesDialog visibility
 * - `confirmDiscard`      — call this when user confirms "Discard"
 * - `cancelDiscard`       — call this when user wants to keep editing
 *
 * Usage:
 * ```tsx
 * const { guardedOnOpenChange, showDiscard, confirmDiscard, cancelDiscard }
 *   = useDirtyGuard(form, onOpenChange, clearDraft);
 * <Sheet open={open} onOpenChange={guardedOnOpenChange}>
 * <DiscardChangesDialog open={showDiscard} onConfirm={confirmDiscard} onCancel={cancelDiscard} />
 * ```
 */
export function useDirtyGuard<T extends FieldValues>(
  form: UseFormReturn<T>,
  onOpenChange: (open: boolean) => void,
  clearDraft?: () => void,
  saveDraftNow?: () => void
) {
  const [showDiscard, setShowDiscard] = useState(false);
  const pendingCloseRef = useRef(false);

  const guardedOnOpenChange = useCallback(
    (nextOpen: boolean) => {
      // Opening the sheet — always allow
      if (nextOpen) {
        onOpenChange(true);
        return;
      }

      // Closing the sheet — check if form is dirty
      if (form.formState.isDirty) {
        pendingCloseRef.current = true;
        setShowDiscard(true);
        return;
      }

      // Not dirty — close immediately
      onOpenChange(false);
    },
    [form.formState.isDirty, onOpenChange]
  );

  const confirmDiscard = useCallback(() => {
    setShowDiscard(false);
    pendingCloseRef.current = false;
    clearDraft?.();
    form.reset();
    onOpenChange(false);
  }, [clearDraft, form, onOpenChange]);

  const cancelDiscard = useCallback(() => {
    setShowDiscard(false);
    pendingCloseRef.current = false;
  }, []);

  /** Close the form while saving current values as a draft (no data loss). */
  const saveDraftAndClose = useCallback(() => {
    setShowDiscard(false);
    pendingCloseRef.current = false;
    saveDraftNow?.();
    onOpenChange(false);
  }, [saveDraftNow, onOpenChange]);

  return {
    guardedOnOpenChange,
    showDiscard,
    confirmDiscard,
    cancelDiscard,
    saveDraftAndClose,
  };
}

export default useDirtyGuard;
